/**
 * @packageDocumentation
 * Declares types and helpers for the in-game settings menu, including toggle wiring and persisted preferences.
 */
/** 
 * Class representing user options and settings menu
 * @extends PIXI.Container
 */
import * as PIXI from "pixi.js";
import { Assets } from 'pixi.js';
import { gsap } from "gsap/gsap-core";
import { brightnessFilter, font_main } from "./global";
import { SoundManager } from "./sounds";
import { SoundPrompt } from "./soundPrompt";
export class UserOptions extends PIXI.Container {
    /** The settings icon button sprite */
    iconButton: PIXI.Sprite;
    /** Container for all settings menu elements */
    public settingsContainer!: PIXI.Container;
    /** Container that controls settings menu scaling */
    public settingsScaleControler!: PIXI.Container;

    // --- Sound prompt modal (reuses preloader UI) ---
    private soundPrompt?: SoundPrompt;
    private soundPromptOpen = false;

    /**
     * Creates an instance of UserOptions
     * Initializes the settings menu and all its components
     */
    constructor() {
        super();

        this.iconButton = new PIXI.Sprite(Assets.get("Info_btn"));
        this.iconButton.anchor.set(0.5);
        this.iconButton.x = -95;
        this.iconButton.y = 12;
        this.iconButton.interactive = true;
        this.iconButton.cursor = "pointer";

        this.iconButton.on("pointertap", () => {
            SoundManager.Btnclick_1SndPlay();
            this.openSoundPrompt();
        });

        this.iconButton.on("pointerover", () => {
            this.iconButton.filters = [brightnessFilter];
        });

        this.iconButton.on("pointerout", () => {
            this.iconButton.filters = [];
        });

        this.addChild(this.iconButton);

        // You can keep these if you still need the legacy settings container elsewhere,
        // but they are no longer used by the hamburger click.
        this.createSettingsContainer();
        this.createMuteBgMusic();
        this.createMuteAllMusic();
        this.creategameInfo();
        this.createClosebtn();
    }


    /**
     * Creates the main container for the settings menu
     * Initializes containers and background
     */
    createSettingsContainer() {

        this.settingsScaleControler = new PIXI.Container();

        this.settingsContainer = new PIXI.Container();
        this.settingsContainer.visible = false;
        let settingsBg = new PIXI.Sprite(Assets.get("popup"));
        settingsBg.anchor.set(0.5);
        settingsBg.scale.set(1);
        this.settingsContainer.addChild(settingsBg);
        this.settingsScaleControler.addChild(this.settingsContainer)
        this.addChild(this.settingsScaleControler)
    }

    /**
     * Creates the game rules button and info page functionality
     */
    creategameInfo() {
        let GameRulesBtn = new PIXI.Sprite(Assets.get("Plain_btn"));
        GameRulesBtn.anchor.set(0.5)
        GameRulesBtn.y = -this.settingsContainer.children[0].height / 4
        let GameRulesBtnText = new PIXI.Text({
            text: "GAME RULES", style: {
                fontFamily: font_main,
                stroke: {
                    color: "black",
                    width: 1,
                    join: "round"

                },
                align: "center",
                fill: 'yellow',
                fontSize: 30,
                fontWeight: 'lighter',
            }
        })
        GameRulesBtnText.anchor.set(0.5)
        GameRulesBtn.addChild(GameRulesBtnText)
        GameRulesBtn.interactive = true
        GameRulesBtn.cursor = "pointer"
        GameRulesBtn.on("pointerover", () => {
            GameRulesBtn.filters = [brightnessFilter]
        })
        GameRulesBtn.on("pointerout", () => {
            GameRulesBtn.filters = []
        })
        GameRulesBtn.on("pointertap", () => {
            SoundManager.Btnclick_1SndPlay();
            document.getElementById('info-page')!.style.display = 'block';
        })
        // this.settingsContainer.addChild(GameRulesBtn)

        const closeBtn = document.getElementById('info-close')!;
        closeBtn.addEventListener('click', () => {
            document.getElementById('info-page')!.style.display = 'none';
        });
    }

    /**
     * Creates the background music mute toggle button
     */
    createMuteBgMusic() {
        let BgMusicframes: PIXI.Texture[] = [Assets.get("fastplay_btn_without_clk"), Assets.get("fastplay_btn_with_clk")]
        let BgMusicController = new PIXI.AnimatedSprite(BgMusicframes);
        BgMusicController.anchor.set(0.5)
        BgMusicController.y = -50
        let BgMusicControllerText = new PIXI.Text({
            text: "MUTE BG MUSIC", style: {
                fontFamily: font_main,
                stroke: {
                    color: "black",
                    width: 2,
                    join: "round"

                },
                align: "center",
                fill: 'white',
                fontSize: 30,
                fontWeight: 'lighter',
            }
        })
        BgMusicControllerText.anchor.set(0.5);
        BgMusicControllerText.x = -20;
        BgMusicController.addChild(BgMusicControllerText)
        BgMusicController.gotoAndStop(0);
        BgMusicController.interactive = true
        BgMusicController.cursor = "pointer"
        BgMusicController.on("pointerover", () => {
            BgMusicController.filters = [brightnessFilter]
        })
        BgMusicController.on("pointerout", () => {
            BgMusicController.filters = []
        })
        BgMusicController.on("pointertap", () => {
            SoundManager.Btnclick_1SndPlay();

            if (BgMusicController.currentFrame == 0) {
                BgMusicController.gotoAndStop(1)
                SoundManager.MuteBgMusicFun()
            } else {
                BgMusicController.gotoAndStop(0)
                SoundManager.PlayBgMusicFun()
            }

        })
        this.settingsContainer.addChild(BgMusicController)

    }

    /**
     * Creates the all sound mute toggle button
     */
    createMuteAllMusic() {
        let MuteAllMusicframes: PIXI.Texture[] = [Assets.get("fastplay_btn_without_clk"), Assets.get("fastplay_btn_with_clk")]
        let MuteAllMusicController = new PIXI.AnimatedSprite(MuteAllMusicframes);
        MuteAllMusicController.anchor.set(0.5)
        MuteAllMusicController.y = this.settingsContainer.children[0].height / 4 - 35
        let MuteAllMusicControllerText = new PIXI.Text({
            text: "MUTE ALL MUSIC", style: {
                fontFamily: font_main,
                stroke: {
                    color: "black",
                    width: 2,
                    join: "round"

                },
                align: "center",
                fill: 'white',
                fontSize: 30,
                fontWeight: 'lighter',
            }
        })
        MuteAllMusicControllerText.anchor.set(0.5)
        MuteAllMusicControllerText.x = -5
        MuteAllMusicControllerText.x = -20;

        MuteAllMusicController.addChild(MuteAllMusicControllerText)
        MuteAllMusicController.gotoAndStop(0);
        MuteAllMusicController.interactive = true
        MuteAllMusicController.cursor = "pointer"
        MuteAllMusicController.on("pointerover", () => {
            MuteAllMusicController.filters = [brightnessFilter]
        })
        MuteAllMusicController.on("pointerout", () => {
            MuteAllMusicController.filters = []
        })
        MuteAllMusicController.on("pointertap", () => {
            SoundManager.Btnclick_1SndPlay();

            if (MuteAllMusicController.currentFrame == 0) {
                MuteAllMusicController.gotoAndStop(1)

                SoundManager.MuteGameSoundFun()
            } else {
                MuteAllMusicController.gotoAndStop(0)
                SoundManager.PlayGameSoundFun()
            }
        })
        this.settingsContainer.addChild(MuteAllMusicController)

    }

    /**
     * Creates the close button for the settings menu
     */
    createClosebtn() {
        let closebtn = new PIXI.Sprite(Assets.get("close_btn"));
        closebtn.anchor.set(0.5)
        closebtn.scale.set(0.6)
        closebtn.interactive = true
        closebtn.cursor = "pointer"
        closebtn.on("pointerover", () => {
            closebtn.filters = [brightnessFilter]
        })
        closebtn.on("pointerout", () => {
            closebtn.filters = []
        })
        closebtn.on("pointertap", () => {
            SoundManager.Btnclick_1SndPlay();
            this.iconButton.interactive = false;
            gsap.killTweensOf(this.settingsContainer)
            gsap.fromTo(this.settingsContainer, { pixi: { scale: 0.9 } }, {
                pixi: { scale: 0 }, ease: "elastic.in(1,0.5)", onComplete: () => {
                    this.iconButton.interactive = true;
                    this.settingsContainer.visible = false;
                }
            })

        })
        closebtn.x = this.settingsContainer.children[0].width / 2 - closebtn.width / 3 - 5
        closebtn.y = -this.settingsContainer.children[0].height / 2 + closebtn.height / 3


        this.settingsContainer.addChild(closebtn)

    }



    private openSoundPrompt() {
        if (this.soundPromptOpen) return;

        const app = (globalThis as any).app ?? (globalThis as any).__PIXI_APP__ ?? undefined;
        if (!app?.stage) return;

        this.soundPromptOpen = true;

        const prompt = new SoundPrompt({
            width: 1596,
            height: 648,
            onChoice: (enabled: boolean) => {
                // Mirror existing project sound controls
                if (enabled) {
                    SoundManager.PlayBgMusicFun();
                    SoundManager.PlayGameSoundFun();
                } else {
                    SoundManager.MuteBgMusicFun();
                    SoundManager.MuteGameSoundFun();
                }

                // Remove + destroy modal
                try {
                    prompt.parent?.removeChild(prompt);
                } catch { }
                prompt.destroy({ children: true });

                this.soundPromptOpen = false;
                this.soundPrompt = undefined;
            },
        });

        prompt.zIndex = 9999;

        // Stage must be sortable for zIndex to apply (safe to set repeatedly)
        (app.stage as any).sortableChildren = true;

        app.stage.addChild(prompt);
        this.soundPrompt = prompt;
    }
}
